import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonIcon from '@mui/icons-material/Person';
import MailIcon from '@mui/icons-material/Mail';
import DownloadIcon from '@mui/icons-material/Download';
import toast from 'react-hot-toast';
import MainContent from '../../components/MainContent';
import Spinner from '../../components/spinner/Spinner';
import TableWrapper from '../../utils/TableWrapper';
import { axiosInstancev2 } from '../../utils/Axios/axiosInterceptorv2';
import { generateHPFReport } from '../../utils/pdfGenerator';

function HPFAssessment() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useSelector((state) => state.auth.userDetails.role);
  const [statusFilter, setStatusFilter] = useState('All');

  const fetchSubmissions = async () => {
    const response = await axiosInstancev2.get(
      '/cms/strapi/hpf-assessment-submissions?populate=*&sort=createdAt:desc&pagination[pageSize]=1000'
    );
    return response.data.data;
  };

  const {
    data: submissions,
    isLoading,
    error,
  } = useQuery(['hpf-submissions'], fetchSubmissions);

  const sendFeedbackMutation = useMutation(
    async ({
      subId,
      email,
      firstname,
      mcqScore,
      practicalScore,
      totalScore,
      overallFeedback,
    }) => {
      await axiosInstancev2.post('/users/hpf-assessment/feedback', {
        email,
        firstname,
        mcq_score: mcqScore,
        practical_score: practicalScore,
        total_score: totalScore,
        status: totalScore >= 70 ? 'PASS' : 'FAIL',
        overall_feedback: overallFeedback,
      });

      return axiosInstancev2.put(
        `/cms/strapi/hpf-assessment-submissions/${subId}`,
        {
          data: { feedbackEmailSent: true },
        }
      );
    },
    {
      onSuccess: () => {
        toast.success('Feedback email sent successfully!');
        queryClient.invalidateQueries(['hpf-submissions']);
      },
      onError: (err) => {
        const errorMsg =
          err.response?.data?.error?.message ||
          err.response?.data?.error ||
          err.message;
        toast.error(`Failed to send feedback: ${errorMsg}`);
      },
    }
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    const gradedSubmissions = submissions?.filter(
      (sub) => (sub.attributes || sub).status === 'Graded'
    );
    if (!gradedSubmissions || gradedSubmissions.length === 0) {
      toast.error('No graded submissions to download.');
      return;
    }

    setIsDownloading(true);
    try {
      await generateHPFReport(gradedSubmissions);
      toast.success('PDF report generated successfully!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF report.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Transform submissions data for TableWrapper
  const transformedData =
    submissions
      ?.filter((sub) => {
        const attr = sub.attributes || sub;
        const status = attr.status || 'Pending';
        return statusFilter === 'All' || status === statusFilter;
      })
      ?.map((sub) => {
        const attr = sub.attributes || sub;
        const mcqScore = attr.mcqScore || 0;
        const practicalScore = attr.practicalScore || 0;
        const finalScore = mcqScore + practicalScore;
        const status = attr.status || 'Pending';
        return {
          id: sub.id,
          documentId: sub.documentId || sub.id,
          fullName: attr.fullName || '',
          email: attr.email || '',
          mcqScore,
          practicalScore,
          finalScore,
          status,
          createdAt: attr.createdAt,
          feedbackEmailSent: attr.feedbackEmailSent || false,
          overallFeedback: attr.overallFeedback,
        };
      }) || [];

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Graded':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Define columns for TableWrapper
  const columns = [
    {
      title: 'Candidate',
      field: 'fullName',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#009688]/10 flex items-center justify-center text-[#009688] font-bold text-xs">
            {row.fullName ? row.fullName.charAt(0) : '?'}
          </div>
          <span className="font-medium text-gray-900">{row.fullName}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      field: 'email',
      render: (row) => (
        <span className="text-gray-600 text-sm">{row.email}</span>
      ),
    },
    {
      title: 'MCQ Score',
      field: 'mcqScore',
      render: (row) => (
        <div className="text-center">
          <span className="text-sm font-medium text-gray-700">
            {row.mcqScore}/25
          </span>
          <span className="text-[10px] block text-gray-400">
            ({Math.round((row.mcqScore / 25) * 100)}%)
          </span>
        </div>
      ),
    },
    {
      title: 'Practical',
      field: 'practicalScore',
      render: (row) => (
        <div className="text-center">
          <span className="text-sm font-medium text-gray-700">
            {row.status === 'Graded' ? `${row.practicalScore}/75` : 'Pending'}
          </span>
          {row.status === 'Graded' && (
            <span className="text-[10px] block text-gray-400">
              ({Math.round((row.practicalScore / 75) * 100)}%)
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Final',
      field: 'finalScore',
      render: (row) => (
        <div className="text-center">
          <span
            className={`text-sm font-bold ${
              row.finalScore >= 70 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {row.status === 'Graded' ? `${row.finalScore}%` : '—'}
          </span>
        </div>
      ),
    },
    {
      title: 'Status',
      field: 'status',
      render: (row) => (
        <div className="text-center">
          <span
            className={`px-3 py-1 rounded-full text-[11px] font-bold border ${getStatusStyle(
              row.status
            )}`}
          >
            {row.status.toUpperCase()}
          </span>
        </div>
      ),
    },
    {
      title: 'Date',
      field: 'createdAt',
      render: (row) => (
        <span className="text-gray-500 text-sm">
          {new Date(row.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      title: 'Actions',
      field: 'actions',
      sorting: false,
      render: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/hpf-assessment/${row.documentId}`)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
        >
          <VisibilityIcon sx={{ fontSize: 16, color: '#009688' }} />
          {row.status === 'Graded' ? 'View' : 'Grade'}
        </button>
      ),
    },
    ...(role === 'Admin' || role === 'SuperAdmin'
      ? [
          {
            title: 'Email Feedback',
            field: 'feedbackEmailSent',
            sorting: false,
            render: (row) => {
              if (row.status !== 'Graded') {
                return (
                  <span className="text-xs text-gray-400 italic">
                    Grade first
                  </span>
                );
              }
              return (
                <button
                  type="button"
                  disabled={
                    row.feedbackEmailSent || sendFeedbackMutation.isLoading
                  }
                  onClick={() =>
                    sendFeedbackMutation.mutate({
                      subId: row.documentId,
                      email: row.email,
                      firstname: row.fullName.split(' ')[0],
                      mcqScore: row.mcqScore,
                      practicalScore: row.practicalScore,
                      totalScore: row.finalScore,
                      overallFeedback: row.overallFeedback,
                    })
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    row.feedbackEmailSent
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-[#009688] text-white hover:bg-[#00796B] shadow-sm active:scale-95'
                  }`}
                >
                  {sendFeedbackMutation.isLoading &&
                  sendFeedbackMutation.variables?.subId === row.documentId ? (
                    <Spinner size="xs" />
                  ) : (
                    <MailIcon sx={{ fontSize: 16 }} />
                  )}
                  {row.feedbackEmailSent ? 'Sent' : 'Send Feedback'}
                </button>
              );
            },
          },
        ]
      : []),
  ];

  if (isLoading) {
    return (
      <MainContent full>
        <div className="flex h-[60vh] items-center justify-center">
          <Spinner />
        </div>
      </MainContent>
    );
  }

  if (error) {
    return (
      <MainContent full>
        <div className="flex h-[60vh] flex-col items-center justify-center text-red-500">
          <ErrorOutlineIcon sx={{ fontSize: 48, mb: 2 }} />
          <p className="text-xl font-semibold">Failed to load submissions</p>
          <p className="text-sm opacity-70">{error.message}</p>
        </div>
      </MainContent>
    );
  }

  const stats = {
    total: submissions?.length || 0,
    pending:
      submissions?.filter((s) => {
        const attr = s.attributes || s;
        return attr.status === 'Pending' || !attr.status;
      }).length || 0,
    graded:
      submissions?.filter((s) => (s.attributes || s).status === 'Graded')
        .length || 0,
  };

  return (
    <MainContent full>
      <div className="space-y-6 p-6 bg-[#F9FAFB] min-h-screen">
        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-xl text-blue-600 flex items-center justify-center">
              <PersonIcon />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">
                Total Submissions
              </p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className="p-3 bg-yellow-50 rounded-xl text-yellow-600 flex items-center justify-center">
              <AccessTimeIcon />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">
                Pending Review
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.pending}
              </p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-xl text-green-600 flex items-center justify-center">
              <CheckCircleIcon />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Graded</p>
              <p className="text-2xl font-bold text-gray-900">{stats.graded}</p>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <FilterListIcon className="text-gray-400" sx={{ fontSize: 18 }} />
            <select
              className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#009688]/20 focus:border-[#009688] transition-all min-w-[140px] w-full md:w-auto"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Graded">Graded</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95 disabled:opacity-50 w-full md:w-auto whitespace-nowrap"
          >
            {isDownloading ? (
              <Spinner size="xs" />
            ) : (
              <DownloadIcon sx={{ fontSize: 20 }} />
            )}
            Download Results PDF
          </button>
        </div>

        {/* Table Section with Pagination */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <TableWrapper
            title="HPF Assessment Submissions"
            data={transformedData}
            columns={columns}
            options={{
              search: true,
              searchFieldPlaceholder: 'Search by name or email...',
              searchFieldVariant: 'outlined',
            }}
          />
        </div>
      </div>
    </MainContent>
  );
}

export default HPFAssessment;
